"use client";

import React, { useEffect } from "react";
import { Clock, FileCheck2, CircleQuestionMark as HelpCircle, PhoneCall, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function Intake() {
  useEffect(() => {
    const cssId = "jobber-work-request-css";
    if (!document.getElementById(cssId)) {
      const link = document.createElement("link");
      link.id = cssId;
      link.rel = "stylesheet";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      link.media = "screen";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <section id="request-service" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-border mb-12">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
            </span>
            <span className="text-xs font-mono font-bold tracking-wider uppercase text-primary">
              24/7 Rapid Intake • Maryland / DC / Northern Virginia
            </span>
          </div>
          <div className="text-xs font-mono text-muted-foreground">
            Direct Technician Dispatch • No Call Centers
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Intake Form (7 cols) */}
          <div className="lg:col-span-7">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm bg-muted text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Emergency & Scheduled Testing
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
                Request hazardous material containment
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                Connect directly with local certified technicians in Maryland, DC, and Northern Virginia. We review your property specifications, coordinate testing, and mobilize negative-pressure equipment without third-party delay.
              </p>
            </div>

            <Card className="border border-border shadow-sm rounded-lg bg-card overflow-hidden">
              <CardContent className="p-6 sm:p-8">
                <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
              </CardContent>
            </Card>
          </div>

          {/* Right Column: 24/7 Phone Dispatch & Pre-Testing Safety Advice (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Primary Direct Call Card */}
            <Card className="border-2 border-primary/30 bg-primary/5 rounded-lg shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-mono font-bold uppercase">
                    <Clock className="h-3.5 w-3.5" />
                    Live 24/7 Dispatch
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">Priority Response</span>
                </div>
                <CardTitle className="text-xl font-bold text-foreground mt-2 font-default">
                  Immediate Hazardous Material Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  If pipes with friable insulation ruptured or renovation activities disturbed older drywall or plaster, phone lines connect directly to regional field supervisors.
                </p>

                <div className="p-4 rounded-md bg-background border border-border">
                  <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
                    Direct Response Phone
                  </div>
                  <a href="tel:855-435-6631" className="text-2xl sm:text-3xl font-mono font-bold text-primary hover:text-primary/80 transition-colors block">
                    855-435-6631
                  </a>
                  <div className="text-xs text-muted-foreground mt-1">
                    Free consultation • Insurance coordination from minute one
                  </div>
                </div>

                <Button asChild className="w-full h-11 bg-primary text-primary-foreground font-bold hover:bg-primary/90">
                  <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                    <PhoneCall className="h-4 w-4" />
                    Call 855-435-6631 Now
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Site Safety Prior to Professional Testing Advisory */}
            <Card className="border border-border bg-card rounded-lg shadow-xs">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 text-destructive">
                  <ShieldAlert className="h-5 w-5" />
                  <h3 className="font-semibold text-base text-foreground font-default">
                    Site Safety Advisory (Pre-Testing)
                  </h3>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Never sweep, vacuum, or scrape materials suspected of containing asbestos or lead. Follow these immediate containment measures until our technicians arrive:
                </p>

                <ul className="space-y-3 text-xs sm:text-sm text-foreground">
                  <li className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 font-mono text-xs font-bold mt-0.5">
                      1
                    </span>
                    <span>
                      <strong className="font-semibold text-foreground">Isolate the affected area:</strong> Shut doors to the room and prevent foot traffic from tracking dust through the property.
                    </span>
                  </li>

                  <li className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 font-mono text-xs font-bold mt-0.5">
                      2
                    </span>
                    <span>
                      <strong className="font-semibold text-foreground">Disable HVAC units:</strong> Turn off central forced-air heating or air conditioning to prevent microscopic fibers from circulating across vents.
                    </span>
                  </li>

                  <li className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0 font-mono text-xs font-bold mt-0.5">
                      3
                    </span>
                    <span>
                      <strong className="font-semibold text-foreground">Avoid domestic vacuums:</strong> Standard HEPA or shop vacs allow microscopic asbestos fibers to exhaust straight into breathable air.
                    </span>
                  </li>
                </ul>

                <div className="pt-2 border-t border-border flex items-center gap-2 text-xs text-muted-foreground font-mono">
                  <FileCheck2 className="h-4 w-4 text-primary shrink-0" />
                  <span>Licensed MD, DC & VA Hazardous Waste Abatement</span>
                </div>
              </CardContent>
            </Card>

            {/* Local Technician Guarantee Box */}
            <div className="p-4 rounded-lg bg-muted/60 border border-border flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground block font-medium mb-0.5">
                  Direct regional accountability
                </strong>
                Every inquiry is assigned to an in-house TriServe mitigation supervisor. We handle required municipal permits, air clearance monitoring, and direct insurance documentation.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}